/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/jsx-no-bind */
import React from 'react';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableVirtuoso, TableComponents } from 'react-virtuoso';
import { ColumnDataProps, RowDataProps } from './props/tables';
import Tooltip from './info-elements/tooltip';
import { renderRowContentType, renderHeight } from './__fixtures__/table-helpers';
import useWindowSize from '../hooks/use-window-size';
import MenuOptions, { type MouseEventLI } from './menus/menu-options';
import { OptionItem } from './cards/card.types';
import Metadata from './metadata';

export interface Props extends TableComponents<RowDataProps> {
  columns: ColumnDataProps[];
  handleRowClick: (row: RowDataProps) => void;
  onRowAction: (option: OptionItem, item: RowOption['item']) => void;
  onFetchMore: () => void;
  height?: number;
  rows?: Record<string, any>[];
}

interface VTableProps { context?: any }

type RowOption = { option: OptionItem; item: Record<any, any> };

function isClickingRow(e) {
  return e.target.getAttribute('class')?.includes('MuiBox-root')
    || e.target.getAttribute('class')?.includes('MuiTableCell-root')
    || e.target.getAttribute('class')?.includes('MuiTableRow-root');
}

/**
 * `Virtualized Table Components` are passed to the TableVirtuoso element as custom
 * components. To avoid generating a fresh instance on each re-render this cannot
 * be inline.
 * @context is a prop passed to the Virtuoso table for accessing props. It is
 * desctructured/separated from the defined props so that it is not added
 * to the DOM.
 *
 * See more: https://virtuoso.dev/custom-scroll-container/#list-with-custom-scroller
 */
const VTableComponents = {
  Scroller: React.forwardRef<HTMLDivElement, VTableProps>(
    ({ context, ...tableProps }, ref) => (
      <TableContainer component={Paper} {...tableProps} ref={ref} />
    ),
  ),
  Table: ({ context, ...tableProps }: Record<string, any> & VTableProps) => (
    <MuiTable
      {...tableProps}
      style={{
        borderCollapse: 'separate',
        tableLayout: 'fixed',
      }}
    />
  ),
  TableHead: React.forwardRef<HTMLTableSectionElement, VTableProps>(
    ({ context, ...tableProps }, ref) => (
      <TableHead
        {...tableProps}
        ref={ref}
        sx={{ backgroundColor: (theme) => theme.palette.common.white }}
      />
    ),
  ),
  TableRow: ({ context, ...rowProps }: Record<string, any> & VTableProps) => (
    <TableRow
      hover
      sx={{ cursor: 'pointer' }}
      data-testid="table-row"
      onClick={(e) => {
        if (isClickingRow(e)) {
          context(rowProps.item);
        }
      }}
      {...rowProps}
    />
  ),
  TableBody: React.forwardRef<HTMLTableSectionElement, VTableProps>(
    ({ context, ...tableProps }, ref) => <TableBody {...tableProps} ref={ref} />,
  ),
};

const TableVirtualized = React.memo(({ ...props }: Props) => {
  const { height } = useWindowSize();

  function fixedHeaderContent() {
    return (
      <TableRow>
        {props.columns.map((column) => (
          <TableCell
            key={column.dataKey}
            variant="head"
            align={column.numeric ? 'right' : 'left'}
            style={{ width: `${column.width}%` }}
          >
            <Tooltip key={column.dataKey} isDisabled={!!column.render}>
              <>{column.label}</>
            </Tooltip>
          </TableCell>
        ))}
        <TableCell sx={{ backgroundColor: 'background.paper', width: 60 }} />
      </TableRow>
    );
  }

  function rowContent(_index: number, row: Record<string, any>) {
    const handleRowAction = (e: MouseEventLI, { option, item }: RowOption, anchor: HTMLButtonElement | null) => {
      e.stopPropagation();
      props.onRowAction(option, item);
    };

    return (
      <>
        {props.columns.map((column) => (
          <TableCell
            size="small"
            style={{ width: `${column.width}%` }}
            key={column.dataKey}
            align={column.numeric ? 'right' : 'left'}
          >
            <Tooltip
              key={column.dataKey}
              isDisabled={!!column.render}
            >
              {renderRowContentType(row, column) as React.ReactElement}
            </Tooltip>
          </TableCell>
        ))}
        <TableCell size="small" align="right" sx={{ width: 60 }}>
          {row.createdBy ? (
            <MenuOptions
              item={row}
              options={row.options ?? []}
              onOptionClick={handleRowAction}
            >
              <Metadata
                createdBy={row.createdBy}
                createdAt={row.createdAt}
                p={2.5}
              />
            </MenuOptions>
          ) : (
            <MenuOptions
              item={row}
              options={row.options ?? []}
              onOptionClick={handleRowAction}
            />
          )}
        </TableCell>
      </>
    );
  }

  const handleBottomStateChange = (isAtBottom: boolean) => {
    if (isAtBottom) {
      props.onFetchMore();
    }
  };

  return (
    <TableVirtuoso
      data={props.rows ?? []}
      atBottomStateChange={handleBottomStateChange}
      components={VTableComponents}
      fixedHeaderContent={fixedHeaderContent}
      itemContent={rowContent}
      context={props.handleRowClick}
      style={{
        height: renderHeight(height, props.rows?.length),
        width: '100%',
        outline: 'none',
      }}
    />
  );
});

export default TableVirtualized;
